import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink } from "react-router-dom";
import { logout } from "../../redux/actions/session";
import { hapticLight, hapticMedium } from "../../utils/haptic";
import { SUPPORT } from "../../utils/constants";
import "./MenuDesktop.scss";

import logo from "../../assets/images/logo-trucoxo.png";
import avatarFallback from "../../assets/images/avatar/avatar20.webp";
import iconPerfil from "../../assets/images/icon-perfil.png";
import iconWhatsapp from "../../assets/images/icon-whatsapp.png";
import iconSair from "../../assets/images/icon-sair.png";

const formatBRL = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return `R$ ${n.toFixed(2).replace(".", ",")}`;
};

function MenuDesktop() {
  const dispatch = useDispatch();
  const score = useSelector((state) => state.session.score);
  const balance = useSelector((state) => state.session.balance);
  const nickname = useSelector((state) => state.session.nickname);
  const token = useSelector((state) => state.session.token);

  const [menuOpen, setMenuOpen] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const menuRef = useRef(null);

  const handleLogout = () => {
    setMenuOpen(false);
    setConfirmingLogout(false);
    dispatch(logout());
  };

  // Fecha o dropdown quando clicar fora
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const balanceLabel = formatBRL(balance);

  return (
    <nav className="nav-desktop">
      <div className="nav-desktop__bar">
        <Link to="/" className="nav-desktop__brand" aria-label="Truco XP" onClick={hapticLight}>
          <img src={logo} alt="Truco XP" />
        </Link>

        {token ? (
          <>
            <div className="nav-desktop__center">
              <NavLink to="/profile" className="nav-desktop__link" onClick={hapticLight}>
                Perfil
              </NavLink>
              <NavLink to="/ranking" className="nav-desktop__link" onClick={hapticLight}>
                <i className="fa-solid fa-chart-simple nav-desktop__link-icon" />
                Ranking
              </NavLink>
            </div>

            <div className="nav-desktop__right">
              {score != null && (
                <span className="nav-desktop__chip">
                  Pontos: <strong>{score}</strong>
                </span>
              )}
              <NavLink to="/withdraw" className="nav-desktop__text-link" onClick={hapticMedium}>
                Sacar
              </NavLink>

              <div className="nav-desktop__profile" ref={menuRef}>
                <button
                  type="button"
                  className="nav-desktop__chip nav-desktop__chip--avatar"
                  onClick={() => { hapticLight(); setMenuOpen((v) => !v); }}
                  aria-expanded={menuOpen}
                >
                  <img
                    className="nav-desktop__chip-avatar"
                    src={avatarFallback}
                    alt=""
                  />
                  <strong>{balanceLabel || "R$ 0,00"}</strong>
                </button>

                {menuOpen && (
                  <div className="nav-desktop__menu">
                    <div className="nav-desktop__menu-header">
                      <img
                        src={avatarFallback}
                        alt=""
                        className="nav-desktop__menu-avatar"
                      />
                      <div className="nav-desktop__menu-identity">
                        {nickname && (
                          <span className="nav-desktop__menu-nickname">
                            {nickname}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="nav-desktop__menu-close"
                        onClick={() => setMenuOpen(false)}
                        aria-label="Fechar"
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </div>

                    <div className="nav-desktop__menu-balance">
                      <div className="nav-desktop__menu-balance-head">
                        <span>Saldo</span>
                        <button
                          type="button"
                          className="nav-desktop__menu-eye"
                          onClick={() => setShowBalance((v) => !v)}
                          aria-label={
                            showBalance ? "Ocultar saldo" : "Mostrar saldo"
                          }
                        >
                          <i
                            className={`fa-solid ${
                              showBalance ? "fa-eye" : "fa-eye-slash"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="nav-desktop__menu-balance-body">
                        <span>Disponível para jogar e sacar</span>
                        <strong>
                          {showBalance ? balanceLabel || "R$ 0,00" : "••••"}
                        </strong>
                      </div>
                    </div>

                    <ul className="nav-desktop__menu-list">
                      <li>
                        <Link
                          to="/profile"
                          className="nav-desktop__menu-item"
                          onClick={() => { hapticLight(); setMenuOpen(false); }}
                        >
                          <img src={iconPerfil} alt="" className="nav-desktop__menu-icon" />
                          <span>Minha conta</span>
                        </Link>
                      </li>
                      <li>
                        <a
                          href={SUPPORT.whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="nav-desktop__menu-item"
                          onClick={() => { hapticLight(); setMenuOpen(false); }}
                        >
                          <img src={iconWhatsapp} alt="" className="nav-desktop__menu-icon" />
                          <span>Suporte</span>
                        </a>
                      </li>
                      <li className="nav-desktop__menu-sep">
                        <button
                          type="button"
                          className="nav-desktop__menu-item"
                          onClick={() => {
                            hapticLight();
                            setMenuOpen(false);
                            setConfirmingLogout(true);
                          }}
                        >
                          <img src={iconSair} alt="" className="nav-desktop__menu-icon" />
                          <span>Sair</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <NavLink to="/payment" className="nav-desktop__cta" onClick={hapticMedium}>
                Depositar
              </NavLink>
            </div>
          </>
        ) : (
          <div className="nav-desktop__right">
            <a
              href={SUPPORT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-desktop__support"
            >
              <i className="fa-brands fa-whatsapp" />
              <span>Suporte</span>
            </a>
            <Link to="/new-account" className="nav-desktop__cta" onClick={hapticMedium}>
              Criar conta
            </Link>
            <Link to="/login" className="nav-desktop__cta nav-desktop__cta--outline" onClick={hapticLight}>
              Entrar
            </Link>
          </div>
        )}
      </div>

      {confirmingLogout && createPortal(
        <div
          className="nav-desktop__confirm-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmingLogout(false);
          }}
        >
          <div className="nav-desktop__confirm">
            <header className="nav-desktop__confirm-header">
              <h3>Você realmente deseja sair?</h3>
              <button
                type="button"
                className="nav-desktop__menu-close"
                onClick={() => setConfirmingLogout(false)}
                aria-label="Fechar"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </header>
            <button
              type="button"
              className="nav-desktop__confirm-danger"
              onClick={handleLogout}
            >
              Sim, quero sair
            </button>
            <button
              type="button"
              className="nav-desktop__confirm-cancel"
              onClick={() => setConfirmingLogout(false)}
            >
              Cancelar
            </button>
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
}

export default MenuDesktop;
